"use client";

import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, Copy, ShieldCheck } from "lucide-react";
import styles from "./login.module.css";

type SetupResult = { qrCode?: string; secret?: string; error?: string };

type Props = { apiBase?: string; brand?: string; loginPath?: string };

export default function MfaEnrollment({ apiBase = "/api/auth", brand = "Jarvis", loginPath = "/login" }: Props) {
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedCodes, setSavedCodes] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const prepareEnrollment = async () => {
      setBusy(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/mfa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
          body: JSON.stringify({ action: "setup" }),
        });
        const result = (await response.json().catch(() => ({}))) as SetupResult;
        if (response.status === 409) {
          window.location.replace("/login");
          return;
        }
        if (!response.ok || !result.qrCode || !result.secret) {
          setError(result.error || (response.status === 401 ? "Sessione scaduta. Accedi di nuovo." : "Configurazione non disponibile."));
          return;
        }
        setQrCode(result.qrCode);
        setSecret(result.secret);
      } catch {
        if (!controller.signal.aborted) setError("Connessione non disponibile. Riprova.");
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    };
    void prepareEnrollment();
    return () => controller.abort();
  }, []);

  async function confirmEnrollment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || code.trim().length !== 6) return;
    setBusy(true);
    setError("");
    try {
        const response = await fetch(`${apiBase}/mfa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        body: JSON.stringify({ action: "confirm", code: code.trim() }),
      });
      const result = (await response.json().catch(() => ({}))) as { recoveryCodes?: string[]; error?: string };
      if (!response.ok || !result.recoveryCodes?.length) {
        setError(result.error || "Codice non valido. Controlla l’orario del telefono e riprova.");
        return;
      }
      setRecoveryCodes(result.recoveryCodes);
    } catch {
      setError("Connessione non disponibile. Riprova.");
    } finally {
      setBusy(false);
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
    } catch {
      setError("Copia non riuscita: seleziona e salva i codici manualmente.");
    }
  }

  if (recoveryCodes.length > 0) {
    return (
      <>
        <div className={styles.eyebrow}><span /> SECONDO FATTORE ATTIVO</div>
        <h1 id="login-title">Quasi <span>fatto</span></h1>
        <p className={styles.intro}>Salva questi codici di recupero: ciascuno si usa una sola volta e non verranno mostrati di nuovo.</p>
        <div className={styles.recoveryCodes} aria-label="Codici di recupero">
          {recoveryCodes.map((item) => <code key={item}>{item}</code>)}
        </div>
        <button className={styles.copyCodes} type="button" onClick={copyRecoveryCodes}>
          <Copy size={15} /> {copied ? "Codici copiati" : "Copia i codici"}
        </button>
        <label className={styles.savedCheck}>
          <input type="checkbox" checked={savedCodes} onChange={(event) => setSavedCodes(event.target.checked)} />
          <span>Li ho salvati in un posto sicuro</span>
        </label>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <button className={styles.submit} type="button" disabled={!savedCodes} onClick={() => window.location.replace(loginPath)}>
          <span>Accedi con Authenticator</span><ArrowRight size={17} />
        </button>
      </>
    );
  }

  return (
    <>
      <div className={styles.eyebrow}><span /> CONFIGURA AUTENTICATOR</div>
      <h1 id="login-title">Proteggi <span>{brand}</span></h1>
      <p className={styles.intro}>Scansiona il QR con Google Authenticator, Microsoft Authenticator o un’app TOTP compatibile. Poi inserisci il codice a 6 cifre.</p>
      {busy && !qrCode ? <div className={styles.qrLoading}>Preparo la configurazione sicura…</div> : null}
      {qrCode ? (
        <div className={styles.qrSetup}>
          <img className={styles.qrImage} src={qrCode} alt={`QR code per configurare ${brand} nell’app Authenticator`} />
          <p>Se non puoi scansionarlo, inserisci questa chiave nell’app:</p>
          <code className={styles.secretKey}>{secret.match(/.{1,4}/g)?.join(" ")}</code>
        </div>
      ) : null}
      {error && !qrCode && <a className={styles.backToLogin} href={loginPath}>Torna al login</a>}
      <form className={styles.form} onSubmit={confirmEnrollment}>
        <label htmlFor="setup-code">Codice di verifica</label>
        <input
          className={styles.otpInput}
          id="setup-code"
          name="setup-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
          disabled={!qrCode || busy}
          required
        />
        {error && <p className={styles.error} role="alert">{error}</p>}
        <button className={styles.submit} type="submit" disabled={!qrCode || busy || code.length !== 6}>
          <span>{busy ? "Verifica in corso…" : "Attiva il secondo fattore"}</span><ShieldCheck size={17} />
        </button>
      </form>
      <div className={styles.secure}><ShieldCheck size={15} /><span>Il segreto resta conservato solo sul server {brand}</span></div>
    </>
  );
}
